import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/patientUtils";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  SummaryEmpty,
  SummaryLoading,
  SummaryTableWrap,
  summaryTableCell,
  summaryTableHead,
} from "./patientSummaryUi";
import type { SummaryTheme } from "./patientSummaryUi";

interface InvoicesTabProps {
  invoicesData: any[];
  loading: boolean;
  theme?: SummaryTheme;
  hidePatientColumn?: boolean;
  onPrintInvoice?: (paymentId: string, treatmentId: string) => void;
  printingPaymentId?: string | null;
}

type InvoiceRow = {
  key: string;
  group: InvoiceGroup;
  payment?: any;
  isSub: boolean;
};

type InvoiceGroup = {
  key: string;
  treatmentId: string;
  baseInvoiceNo: string;
  fullName: string;
  total: number;
  paid: number;
  due: number;
  updatedAt?: string;
  subPayments: any[];
};

const getUpdatedTime = (value?: string) => (value ? new Date(value).getTime() : 0);

const getPaymentStatus = (due: number) => {
  if (due === 0) return "Paid";
  if (due > 0) return "Due";
  return "Advance";
};

const StatusBadge: React.FC<{ status: string; theme: SummaryTheme }> = ({ status, theme }) => (
  <span
    className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
      status === "Paid"
        ? theme === "dark"
          ? "bg-emerald-500/20 text-emerald-300"
          : "bg-emerald-100 text-emerald-700"
        : status === "Due"
          ? theme === "dark"
            ? "bg-red-500/20 text-red-300"
            : "bg-red-100 text-red-700"
          : theme === "dark"
            ? "bg-amber-500/20 text-amber-300"
            : "bg-amber-100 text-amber-700"
    }`}
  >
    {status}
  </span>
);

const InvoicesTab: React.FC<InvoicesTabProps> = ({
  invoicesData,
  loading,
  theme = "light",
  hidePatientColumn = false,
  onPrintInvoice,
  printingPaymentId,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const invoiceGroups = useMemo(() => {
    const grouped = new Map<string, any[]>();

    invoicesData.forEach((item) => {
      const treatmentId = String(item?.treatmentId ?? item?._id ?? "");
      if (!treatmentId) return;
      const existing = grouped.get(treatmentId) ?? [];
      existing.push(item);
      grouped.set(treatmentId, existing);
    });

    return Array.from(grouped.entries())
      .map(([treatmentId, payments]) => {
        const sortedPayments = [...payments].sort(
          (a, b) => getUpdatedTime(b.updatedAt ?? b.createdAt) - getUpdatedTime(a.updatedAt ?? a.createdAt)
        );
        const total = sortedPayments.reduce((sum, payment) => sum + Number(payment?.total || 0), 0);
        const paid = sortedPayments.reduce((sum, payment) => sum + Number(payment?.paid || 0), 0);
        const due = sortedPayments.reduce((sum, payment) => sum + Number(payment?.due || 0), 0);

        return {
          key: treatmentId,
          treatmentId,
          baseInvoiceNo: sortedPayments[0]?.baseInvoiceNo || sortedPayments[0]?.invoiceNo || "—",
          fullName: sortedPayments[0]?.fullName,
          total,
          paid,
          due,
          updatedAt: sortedPayments[0]?.updatedAt ?? sortedPayments[0]?.createdAt,
          subPayments: sortedPayments,
        } satisfies InvoiceGroup;
      })
      .sort((a, b) => getUpdatedTime(b.updatedAt) - getUpdatedTime(a.updatedAt));
  }, [invoicesData]);

  const visibleRows = useMemo(() => {
    const rows: InvoiceRow[] = [];

    invoiceGroups.forEach((group) => {
      rows.push({
        key: group.key,
        group,
        isSub: false,
      });

      if (expanded[group.key] && group.subPayments.length > 1) {
        group.subPayments.forEach((payment) => {
          rows.push({
            key: `${group.key}-${payment._id}`,
            group,
            payment,
            isSub: true,
          });
        });
      }
    });

    return rows;
  }, [invoiceGroups, expanded]);

  const toggleExpanded = (rowKey: string) => {
    setExpanded((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  if (loading) return <SummaryLoading theme={theme} />;

  if (invoiceGroups.length === 0) {
    return <SummaryEmpty message="No billing records." theme={theme} />;
  }

  const head = summaryTableHead(theme);
  const cell = summaryTableCell(theme);

  return (
    <SummaryTableWrap>
      <table className="w-full min-w-[600px] border-separate border-spacing-0">
        <thead>
          <tr>
            <th className={`${head} w-10 rounded-tl-lg`} aria-label="Expand" />
            <th className={head}>Invoice</th>
            {!hidePatientColumn && <th className={head}>Patient</th>}
            <th className={head}>Total</th>
            <th className={head}>Paid</th>
            <th className={head}>Balance</th>
            <th className={head}>Status</th>
            <th className={head}>Date</th>
            <th className={`${head} text-center rounded-tr-lg`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ key, group, payment, isSub }, index) => {
            const entry = isSub ? payment : group;
            const invoiceNo = isSub ? payment?.invoiceNo : group.baseInvoiceNo;
            const total = Number(entry?.total || 0);
            const paid = Number(entry?.paid || 0);
            const due = Number(entry?.due || 0);
            const status = getPaymentStatus(due);
            const isLast = index === visibleRows.length - 1;
            const hasSubPayments = !isSub && group.subPayments.length > 1;
            const isExpanded = Boolean(expanded[key]);
            const dateValue = isSub
              ? payment?.updatedAt ?? payment?.createdAt
              : group.updatedAt;
            const paymentForPrint = isSub
              ? payment
              : group.subPayments.length === 1
                ? group.subPayments[0]
                : null;

            return (
              <tr
                key={key}
                className={`border-t ${
                  isSub
                    ? theme === "dark"
                      ? "border-slate-700/50 bg-emerald-950/20 hover:bg-emerald-950/30"
                      : "border-slate-100 bg-emerald-50/40 hover:bg-emerald-50/70"
                    : theme === "dark"
                      ? "border-slate-700/50 hover:bg-slate-800/40"
                      : "border-slate-100 hover:bg-slate-50/80"
                }`}
              >
                <td className={`${cell} text-center ${isLast ? "rounded-bl-lg" : ""}`}>
                  {hasSubPayments ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(key);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Hide sub payments" : "Show sub payments"}
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                        theme === "dark"
                          ? "text-emerald-300 hover:bg-emerald-500/20"
                          : "text-emerald-700 hover:bg-emerald-100"
                      }`}
                    >
                      {isExpanded ? (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      )}
                    </button>
                  ) : isSub ? (
                    <span className="inline-block w-8" aria-hidden />
                  ) : null}
                </td>
                <td
                  className={`${cell} ${isSub ? "pl-8" : ""} ${
                    isSub
                      ? theme === "dark"
                        ? "text-emerald-200"
                        : "text-emerald-900"
                      : `font-medium ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`
                  }`}
                >
                  {isSub ? (
                    `└ ${invoiceNo || "—"}`
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span>{invoiceNo || "—"}</span>
                      {hasSubPayments ? (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            theme === "dark"
                              ? "bg-emerald-500/20 text-emerald-300"
                              : "bg-emerald-100 text-emerald-800"
                          }`}
                        >
                          {group.subPayments.length}
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                {!hidePatientColumn && <td className={cell}>{group.fullName || "—"}</td>}
                <td className={cell}>Rs {total.toLocaleString()}</td>
                <td className={cell}>Rs {paid.toLocaleString()}</td>
                <td
                  className={`${cell} font-semibold ${
                    due > 0 ? (theme === "dark" ? "text-red-300" : "text-red-600") : ""
                  }`}
                >
                  Rs {due.toLocaleString()}
                </td>
                <td className={cell}>
                  <StatusBadge status={status} theme={theme} />
                </td>
                <td className={cell}>
                  {dateValue ? formatDate(String(dateValue).split("T")[0]) : "—"}
                </td>
                <td className={`${cell} text-center ${isLast ? "rounded-br-lg" : ""}`}>
                  <div className="flex items-center justify-center gap-1">
                    {!isSub ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (group.treatmentId) {
                            navigate(`/client/payments/invoice/${group.treatmentId}`);
                          }
                        }}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                          theme === "dark"
                            ? "text-indigo-300 hover:bg-indigo-500/20"
                            : "text-indigo-600 hover:bg-indigo-50"
                        }`}
                        title="View invoice"
                      >
                        <VisibilityIcon sx={{ fontSize: 17 }} />
                      </button>
                    ) : null}
                    {paymentForPrint && onPrintInvoice ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPrintInvoice(paymentForPrint._id, group.treatmentId);
                        }}
                        disabled={printingPaymentId === paymentForPrint._id}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:cursor-wait disabled:opacity-50 ${
                          theme === "dark"
                            ? "text-emerald-300 hover:bg-emerald-500/20"
                            : "text-emerald-600 hover:bg-emerald-50"
                        }`}
                        title="Print invoice"
                        aria-label="Print invoice"
                      >
                        {printingPaymentId === paymentForPrint._id ? (
                          <span className="inline-block h-[15px] w-[15px] animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <PrintIcon sx={{ fontSize: 17 }} />
                        )}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SummaryTableWrap>
  );
};

export default InvoicesTab;
